'use client';

import { showStaleBundlePrompt } from '@/hooks/use-toast';
import { isStaleBundleValue } from '@/lib/utils/stale-bundle-patterns';

// The pure detector (STALE_BUNDLE_PATTERNS + isStaleBundleError) lives in the zero-import
// stale-bundle-patterns module so use-toast can gate raw text without an import cycle.
// Re-exported so existing importers (ServerActionRecovery, lazy-with-stale-recovery) are unchanged.
export { isStaleBundleError } from '@/lib/utils/stale-bundle-patterns';

/**
 * Show a user-controlled refresh toast for stale bundle errors.
 *
 * Does NOT auto-reload. Auto-reload during a Next.js navigation transition
 * reloads the SOURCE url because pushState commits only at RSC commit time —
 * if the fetch fails before commit, reload returns the user to the previous
 * page, producing the "phantom-nav" snap-back symptom.
 *
 * Replaces three previous redundant auto-reload paths in ServerActionRecovery,
 * get-query-client, and reporter.ts.
 *
 * Copy + dedupe live in `showStaleBundlePrompt` (use-toast) — ONE prompt app-wide.
 * Pass `draftKept: true` only when the caller actually persisted the user's work
 * (e.g. a stashed task draft), so the prompt never promises a save that didn't happen.
 *
 * @returns true if a stale bundle error was detected and user was notified
 */
export function notifyStaleBundle(error: unknown, options?: { draftKept?: boolean }): boolean {
  if (typeof window === 'undefined') return false;
  if (!isStaleBundleValue(error)) return false;

  showStaleBundlePrompt({ draftKept: options?.draftKept === true });
  return true;
}
